<script>
    import { createEventDispatcher } from "svelte";
    import { mqttClients, redisDevices, loadBrokerStatus } from "../../../service/mqtt";

    export let selectedDeviceId = "";

    const dispatch = createEventDispatcher();
    let keyword = "";
    let activeTab = "mqtt"; // "mqtt" or "redis"
    let isLoading = false;

    // MQTT 탭: clients 배열 (브로커 연결 클라이언트)
    $: mqttList = $mqttClients
        .filter(c => c.mqttId) // mqttId가 있는 것만 (디바이스)
        .sort((a, b) => (a.mqttId || "").localeCompare(b.mqttId || ""));

    // Redis 탭: devices map → 배열
    $: redisList = Object.values($redisDevices).sort((a, b) => {
        if (a.online === "true" && b.online !== "true") return -1;
        if (a.online !== "true" && b.online === "true") return 1;
        return (a.name || "").localeCompare(b.name || "");
    });

    // 현재 탭에 따른 필터링
    $: currentList = activeTab === "mqtt" ? mqttList : redisList;

    $: filteredList = currentList.filter((item) => {
        if (!keyword) return true;
        const k = keyword.toLowerCase();
        if (activeTab === "mqtt") {
            return (
                (item.clientId || "").toLowerCase().includes(k) ||
                (item.mqttId || "").toLowerCase().includes(k) ||
                (item.remote || "").toLowerCase().includes(k)
            );
        } else {
            return (
                (item.name || "").toLowerCase().includes(k) ||
                (item.mqtt_id || "").toLowerCase().includes(k) ||
                (item.ip_address || "").toLowerCase().includes(k)
            );
        }
    });

    function selectItem(item) {
        const id = activeTab === "mqtt" ? item.mqttId : item.mqtt_id;
        dispatch("select", id);
    }

    function getItemId(item) {
        return activeTab === "mqtt" ? item.mqttId : item.mqtt_id;
    }

    async function refresh() {
        isLoading = true;
        await loadBrokerStatus();
        isLoading = false;
    }

    async function switchTab(tab) {
        activeTab = tab;
        await refresh();
    }
</script>

<div class="card h-100">
    <div class="card-header p-2">
        <!-- MQTT / Redis 탭 버튼 -->
        <div class="btn-group w-100 mb-2" role="group">
            <button
                type="button"
                class="btn btn-sm {activeTab === 'mqtt' ? 'btn-primary' : 'btn-outline-primary'}"
                on:click={() => switchTab('mqtt')}
            >
                <i class="bx bx-broadcast me-1"></i>MQTT
            </button>
            <button
                type="button"
                class="btn btn-sm {activeTab === 'redis' ? 'btn-danger' : 'btn-outline-danger'}"
                on:click={() => switchTab('redis')}
            >
                <i class="bx bx-data me-1"></i>Redis
            </button>
        </div>

        <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-2">
                <small class="text-muted">
                    {activeTab === "mqtt" ? "Connected Clients" : "Stored Devices"}
                </small>
                <span
                    class="badge {activeTab === 'mqtt' ? 'bg-primary' : 'bg-danger'} d-flex align-items-center justify-content-center"
                    style="min-width: 24px; height: 20px; cursor: pointer;"
                    on:click={refresh}
                    on:keydown={(e) => e.key === 'Enter' && refresh()}
                    role="button"
                    tabindex="0"
                    title="Reload"
                >
                    {#if isLoading}
                        <i class="bx bx-loader-alt bx-spin" style="font-size: 0.75rem;"></i>
                    {:else}
                        {filteredList.length}
                    {/if}
                </span>
            </div>
        </div>
    </div>

    <div class="p-2 border-bottom">
        <input
            type="text"
            class="form-control form-control-sm"
            placeholder="Search..."
            bind:value={keyword}
        />
    </div>

    <div
        class="list-group list-group-flush overflow-auto"
        style="max-height: 600px;"
    >
        {#if activeTab === "mqtt"}
            <!-- MQTT 탭: 브로커 연결 클라이언트 -->
            {#each filteredList as client (client.clientId)}
                <button
                    class="list-group-item list-group-item-action d-flex align-items-center justify-content-between {selectedDeviceId === client.mqttId ? 'active' : ''}"
                    on:click={() => selectItem(client)}
                >
                    <div class="d-flex align-items-center">
                        <span
                            class="badge rounded-pill bg-success me-2 p-1"
                            style="width: 10px; height: 10px;"
                            title="Connected"
                        ></span>
                        <i class="bx bx-plug me-2 text-success" style="font-size: 1.2rem;"></i>
                        <div>
                            <div class="fw-bold text-truncate" style="max-width: 140px;">
                                {client.clientId}
                            </div>
                            <small class:text-white-50={selectedDeviceId === client.mqttId} class:text-muted={selectedDeviceId !== client.mqttId}>
                                {client.mqttId || "-"}
                            </small>
                        </div>
                    </div>
                    <small class:text-white-50={selectedDeviceId === client.mqttId} class:text-muted={selectedDeviceId !== client.mqttId}>
                        {client.remote?.split(":")[0] || "-"}
                    </small>
                </button>
            {/each}
        {:else}
            <!-- Redis 탭: 저장된 디바이스 -->
            {#each filteredList as device (device.mqtt_id)}
                <button
                    class="list-group-item list-group-item-action d-flex align-items-center justify-content-between {selectedDeviceId === device.mqtt_id ? 'active' : ''}"
                    on:click={() => selectItem(device)}
                >
                    <div class="d-flex align-items-center">
                        <span
                            class="badge rounded-pill me-2 p-1"
                            class:bg-success={device.online === "true"}
                            class:bg-secondary={device.online !== "true"}
                            style="width: 10px; height: 10px;"
                            title={device.online === "true" ? "Online" : "Offline"}
                        ></span>
                        <i
                            class="bx bx-bulb me-2 {device.online !== 'true' ? 'text-muted' : device.power === 'ON' ? 'text-warning' : 'text-secondary'}"
                            style="font-size: 1.2rem;"
                        ></i>
                        <div>
                            <div class="fw-bold text-truncate" style="max-width: 140px;">
                                {device.name || device.mqtt_id}
                            </div>
                            <small class:text-white-50={selectedDeviceId === device.mqtt_id} class:text-muted={selectedDeviceId !== device.mqtt_id}>
                                {device.mqtt_id}
                            </small>
                        </div>
                    </div>
                    {#if device.online === "true"}
                        <small class:text-white-50={selectedDeviceId === device.mqtt_id} class:text-muted={selectedDeviceId !== device.mqtt_id}>
                            {device.ip_address || "-"}
                        </small>
                    {/if}
                </button>
            {/each}
        {/if}

        {#if isLoading}
            <div class="text-center p-3 text-muted">
                <i class="bx bx-loader-alt bx-spin me-1"></i>Loading...
            </div>
        {:else if filteredList.length === 0}
            <div class="text-center p-3 text-muted">
                {activeTab === "mqtt" ? "No connected clients" : "No devices found"}
            </div>
        {/if}
    </div>
</div>

<style>
    .list-group-item.active .text-muted {
        color: rgba(255, 255, 255, 0.7) !important;
    }
</style>
